/**
 * Pure breathing-phase timing logic (unit tested).
 * Maps elapsed session time to the current phase of an exercise cycle.
 */
import type { BreathPhase, BreathingExercise } from './exercises'

export type PhasePosition = {
  phase: BreathPhase
  /** Index of the phase within one cycle. */
  phaseIndex: number
  /** How many full cycles were completed before this one. */
  cycleIndex: number
  /** Seconds elapsed inside the current phase. */
  phaseElapsed: number
  /** Progress through the current phase, 0..1. */
  phaseProgress: number
  /** Seconds elapsed inside the current cycle. */
  cycleElapsed: number
}

/** Total duration of one full breathing cycle, in seconds. */
export function cycleSeconds(exercise: BreathingExercise): number {
  return exercise.phases.reduce((total, phase) => total + phase.seconds, 0)
}

/** Position inside the phase pattern at `elapsedSeconds` into the session. */
export function phaseAt(exercise: BreathingExercise, elapsedSeconds: number): PhasePosition {
  const cycle = cycleSeconds(exercise)
  const safeElapsed = Math.max(0, elapsedSeconds)
  const cycleIndex = Math.floor(safeElapsed / cycle)
  let cycleElapsed = safeElapsed - cycleIndex * cycle
  // Guard against floating point drift pushing us onto the exact cycle edge.
  if (cycleElapsed >= cycle) cycleElapsed = 0

  for (let phaseIndex = 0; phaseIndex < exercise.phases.length; phaseIndex += 1) {
    const phase = exercise.phases[phaseIndex]
    if (cycleElapsed < phase.seconds) {
      return {
        phase,
        phaseIndex,
        cycleIndex,
        phaseElapsed: cycleElapsed,
        phaseProgress: phase.seconds > 0 ? cycleElapsed / phase.seconds : 1,
        cycleElapsed,
      }
    }
    cycleElapsed -= phase.seconds
  }

  // Unreachable with a non-empty pattern, but keep a safe fallback.
  const last = exercise.phases[exercise.phases.length - 1]
  return {
    phase: last,
    phaseIndex: exercise.phases.length - 1,
    cycleIndex,
    phaseElapsed: last.seconds,
    phaseProgress: 1,
    cycleElapsed: cycle,
  }
}

export const CIRCLE_MIN_SCALE = 0.55

/**
 * Target scale of the breathing circle for a position.
 * Grows while inhaling, shrinks while exhaling, holds otherwise.
 */
export function circleScale(position: PhasePosition): number {
  const span = 1 - CIRCLE_MIN_SCALE
  switch (position.phase.kind) {
    case 'in':
      return CIRCLE_MIN_SCALE + span * position.phaseProgress
    case 'hold':
      return 1
    case 'out':
      return 1 - span * position.phaseProgress
    case 'rest':
      return CIRCLE_MIN_SCALE
  }
}

/** Number of fully completed breathing cycles. */
export function completedCycles(exercise: BreathingExercise, elapsedSeconds: number): number {
  return Math.floor(Math.max(0, elapsedSeconds) / cycleSeconds(exercise))
}

/** mm:ss formatting for remaining/elapsed time. */
export function formatMinutesSeconds(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/** Human Dutch duration, e.g. "2 minuten" or "45 seconden". */
export function formatDurationNl(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds))
  if (safe >= 60 && safe % 60 === 0) {
    const minutes = safe / 60
    return minutes === 1 ? '1 minuut' : `${minutes} minuten`
  }
  if (safe >= 60) {
    const minutes = Math.floor(safe / 60)
    const rest = safe % 60
    return `${minutes} min ${rest} sec`
  }
  return `${safe} seconden`
}
